
import React, { useEffect, useReducer, useState } from 'react';
import { useNavigation } from '@/contexts';
import Navigation from '@/components/Navigation/Navigation';
import LoggedOutTopNav from '@/components/LayoutWrapper/LoggedOutTopNav';
import ExploreHeader from '@/components/ExploreHeader/ExploreHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, usePlatformBrowseMode } from '@/contexts';
import { isOpenBrowsePublicPath } from '@/constants/platformBrowseMode';
import { debug } from '@/utils/logger';
import { getAndClearPostAuthRedirect } from '@/lib/postAuthRedirect';
import { isNativePlatform } from '@/lib/platform';

import './LayoutWrapper.css';
import Spinner from '../Spinner/Spinner';
import HiddenUserBanner from '../HiddenUserBanner/HiddenUserBanner';
import MobileTabBar from '@/components/MobileTabBar/MobileTabBar';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import {
    isLoggedOutNativeWelcomeEligible,
    isLoggedOutMobileExplorePersisted,
    LOGGED_OUT_MOBILE_EXPLORE_CLEARED_EVENT,
} from '@/constants/loggedOutHomeQuery';
import { isOwnProfilePath, shouldShowMobileHeaderJourneyCta } from '@/constants/mobileHeaderRoutes';
import { usePushOptIn } from '@/contexts/PushOptInContext';
import { getPlatform } from '@/lib/platform';
import { isOnboardingDeferred } from '@/lib/onboardingDeferral';
import { ExploreHeaderExtensionProvider } from '@/contexts/ExploreHeaderExtensionContext';

interface LayoutWrapperProps {
    children: React.ReactNode;
}
const LayoutWrapper: React.FC<LayoutWrapperProps> = ({ children }) => {
    const { user, isLoading, hasOnboarded } = useAuth();
    const { isOpenBrowse, isLoading: isBrowseModeLoading } = usePlatformBrowseMode();
    const router = useNavigate();
    const rawPathname = useLocation().pathname;
    const isMobile = useIsMobileViewport();
    // The mobile build sets trailingSlash: true (required for static export), so
    // usePathname returns e.g. "/onboarding/" instead of "/onboarding". Strict
    // equality checks below would silently fail and put the app into a redirect
    // loop. Normalize once here.
    const pathname = rawPathname && rawPathname !== '/' ? rawPathname.replace(/\/$/, '') : rawPathname;
    const { isNavigationVisible, toggleNavigation } = useNavigation();
    const { maybeRequestOptIn } = usePushOptIn();
    const isNative = isNativePlatform();

    const isOnboardingPage = pathname === '/onboarding';
    // Logged-out native mobile `/` is full-bleed welcome only until user taps Continue to Explore.
    // Mobile web on `/` skips welcome and goes directly to Explore with regular shell chrome.
    const isLoggedOutWelcomeHome =
        pathname === '/' &&
        isLoggedOutNativeWelcomeEligible({
            hasUser: !!user,
            isMobileViewport: isMobile,
            isNativePlatform: isNative,
        }) &&
        !isLoggedOutMobileExplorePersisted();
    const hideHeaderAndNav =
        pathname === '/onboarding' || pathname === '/auth/callback' || isLoggedOutWelcomeHome;

    // Mobile detail routes: hide top bar chrome (logo + bell) — hero bands provide
    // their own HeroBackButton so the user can still navigate back.
    // The Advisor Pro upgrade flow is the same deal: each screen renders its own
    // back + title nav row (sales page) or back + step nav (onboarding).
    const isAdvisorProFlow =
        pathname === '/advisor-pro' || pathname.startsWith('/advisor-pro/');
    // Own profile: the profile hero renders its own share + menu row, so the top bar
    // goes too. ExploreHeader stays mounted (bar hidden) to host the "More" drawer.
    const isMobileOwnProfileRoute =
        isMobile && !hideHeaderAndNav && isOwnProfilePath(pathname, user?._id);
    const isMobileDetailRoute = isMobile && !hideHeaderAndNav && (
        pathname.startsWith('/list/') ||
        pathname.startsWith('/itinerary/') ||
        pathname.startsWith('/place/') ||
        isAdvisorProFlow ||
        isMobileOwnProfileRoute
    );
    const showTopBar = !hideHeaderAndNav && !isMobileDetailRoute;
    // ExploreHeader's lg+ row only ever duplicated Explore/My Trips/Search/Book/Create/
    // Profile/Log In, all of which now live in the nav rail (Navigation.tsx) — so on web
    // the header row is dropped entirely, for both logged-in and logged-out visitors
    // (nav rail stays visible; mobile keeps its own chrome).
    const hideExploreHeaderOnWeb = !isMobile;
    // Public routes that don't require authentication
    // Note: /onboarding is NOT public - it's only accessible after login when user hasn't completed onboarding
    // Note: /login is public - it redirects to / with openLogin=true, so it needs to render first
    // Note: /results is NOT public - it's only accessible after login for viewing search results
    const publicRoutes = ['/auth/callback', '/', '/faq', '/redirect', '/login', '/creators', '/destinations', '/travel-styles', '/advisors', '/trips'];
    // Also allow /post/[id], /profile/[id], /s/[code], /list/[id], /place/[id], and /itinerary/[id] routes
    // (public but viewing may require a share token in CLOSED mode — access control is handled in route components)
    const isPublicRoute =
        publicRoutes.includes(pathname) ||
        pathname.startsWith('/post/') ||
        pathname.startsWith('/profile/') ||
        pathname.startsWith('/storefront/') ||
        pathname.startsWith('/book/') ||
        pathname.startsWith('/s/') ||
        pathname.startsWith('/list/') ||
        pathname.startsWith('/place/') ||
        pathname.startsWith('/itinerary/') ||
        (isOpenBrowse && isOpenBrowsePublicPath(pathname));
    const deferGuestRedirectForBrowseMode =
        isBrowseModeLoading && !user && isOpenBrowsePublicPath(pathname);

    // Share links and other token-gated content URLs must paint while auth is still resolving.
    // Otherwise a slow or stuck session sync blocks the whole app behind the global Spinner — in
    // particular `/s/[code]` never mounts its resolver. Home and login still wait so Explore vs
    // Welcome / onboarding redirects stay coherent.
    const showGlobalAuthSpinner =
        isLoading && (!isPublicRoute || pathname === '/' || pathname === '/login');

    // Initialize shouldRender based on whether we're on a public route
    // This prevents redirect loops for unauthenticated users on public routes
    const [shouldRender, setShouldRender] = useState(() => {
        // If we're on a public route, allow rendering immediately (will be validated in useEffect)
        return isPublicRoute;
    });
    const [, forceLoggedOutShellUpdate] = useReducer((x: number) => x + 1, 0);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const bump = () => {
            forceLoggedOutShellUpdate();
        };
        window.addEventListener(LOGGED_OUT_MOBILE_EXPLORE_CLEARED_EVENT, bump);
        return () => window.removeEventListener(LOGGED_OUT_MOBILE_EXPLORE_CLEARED_EVENT, bump);
    }, []);

    useEffect(() => {
        if (isLoading) {
            // While loading, keep current render state for public routes
            if (isPublicRoute) {
                setShouldRender(true);
            }
            return;
        }


        // Unauthenticated user trying to access onboarding - redirect to logged-out view
        // Onboarding is only accessible after login when system detects user hasn't completed onboarding
        if (!user && isOnboardingPage) {
            debug('🔐 [LayoutWrapper] Unauthenticated user trying to access /onboarding - redirecting to /', {
                pathname,
                hasUser: !!user,
                isLoading
            });
            setTimeout(() => {
                router('/', { replace: true });
            }, 100);
            setShouldRender(false);
            return;
        }

        // Allow unauthenticated users to freely navigate between public routes
        // Public routes: /, /auth/callback, /faq, /redirect, /post/[id], /profile/[id]
        if (!user && isPublicRoute) {
            setShouldRender(true);
            return;
        }

        // User is NOT logged in and is on a protected page
        // Redirect to logged-out landing page (not onboarding - onboarding is only after login)
        if (!user && !isPublicRoute) {
            if (deferGuestRedirectForBrowseMode) {
                setShouldRender(true);
                return;
            }
            debug('🔐 [LayoutWrapper] Redirecting unauthenticated user to /', {
                pathname,
                isPublicRoute,
            });
            setTimeout(() => {
                router('/', { replace: true });
            }, 100);
            setShouldRender(false);
            return;
        }

        // User IS logged in but hasn't completed onboarding - redirect to onboarding
        // This prevents authenticated but non-onboarded users from accessing public routes like /
        // Skipped for the rest of the session when onboarding was deferred (a guest who signed up
        // just to send an advisor trip request).
        if (user && !hasOnboarded && !isOnboardingPage && !isOnboardingDeferred()) {
            debug('🔐 [LayoutWrapper] Authenticated user has NOT completed onboarding - redirecting to /onboarding', {
                pathname,
            });
            router('/onboarding', { replace: true });
            return;
        }

        // User IS logged in and is on the onboarding page
        if (user && isOnboardingPage) {
            if (hasOnboarded) {
                const destination = getAndClearPostAuthRedirect() ?? '/';
                debug('🔐 [LayoutWrapper] User has completed onboarding - redirecting to', destination);
                router(destination, { replace: true });
                setShouldRender(false);
                return;
            } else {
                setShouldRender(true);
                return;
            }
        }

        setShouldRender(true);
    }, [user, isLoading, hasOnboarded, isOnboardingPage, isPublicRoute, router, pathname, deferGuestRedirectForBrowseMode]);

    useEffect(() => {
        if (getPlatform() !== 'ios') return;
        if (!user || !hasOnboarded || !shouldRender || hideHeaderAndNav) return;
        void maybeRequestOptIn({ entryPoint: 'main_shell' });
    }, [user, hasOnboarded, shouldRender, hideHeaderAndNav, maybeRequestOptIn]);

    if (showGlobalAuthSpinner) {
        return <Spinner />;
    }

    if (!shouldRender) {
        return <Spinner />;
    }

    // Pages that manage their own hero/scroll container — transparent header, no padding — transparent header, no padding
    const hasOwnHeroScroll = pathname === '/'
      || pathname === '/creators'
      || pathname === '/advisors'
      || pathname === '/destinations'
      || pathname === '/travel-styles'
      || pathname === '/trips';

    // All other visible pages need light header + top inset to clear the fixed ExploreHeader
    const needsLightHeader = showTopBar && !hasOwnHeroScroll;
    // Signed-out home has its own hero panel with primary CTAs, so drop the
    // header-level "Let's plan…" caption + Import Travel Inspo button there.
    const showMobileJourneyCta = shouldShowMobileHeaderJourneyCta(pathname) && !(pathname === '/' && !user);
    // Signed-out at md+ gets a top marketing nav instead of the persistent
    // left nav rail. Native builds still take the rail (session context is
    // different — welcome flow, not marketing).
    const showLoggedOutTopNav = !user && !isMobile && showTopBar && !isNativePlatform();
    const showNavRail = showTopBar && !showLoggedOutTopNav;

    // Advisor Pro upgrade flow is a focused task: no tab bar, so the CTA stays
    // the only bottom action.
    const showMobileTabBar = isMobile && !hideHeaderAndNav && !isAdvisorProFlow;

    const needsHeaderInset = showTopBar && !hasOwnHeroScroll && !hideExploreHeaderOnWeb;

    return (
        <ExploreHeaderExtensionProvider>
        <div className={`app-container no-scroll${showNavRail && !isMobile ? ' nav-rail-active' : ''}`}>
            {/* Show banner for hidden users */}
            {user?.is_hidden ? <HiddenUserBanner /> : null}

            {showLoggedOutTopNav && <LoggedOutTopNav />}
            {showTopBar && (
                <>
                    {!hideExploreHeaderOnWeb && (
                        <ExploreHeader
                          variant={needsLightHeader ? 'light' : 'transparent'}
                          showMobileJourneyCta={showMobileJourneyCta}
                        />
                    )}
                    {showNavRail && <Navigation isVisible={isNavigationVisible} onClose={toggleNavigation} />}
                </>
            )}
            {isMobileOwnProfileRoute && <ExploreHeader hideMobileBar />}
              <main
                className={`app-main${needsHeaderInset ? ' explore-fixed-header-top-inset' : ''}`}
                style={{
                  paddingTop: isMobileDetailRoute ? 'env(safe-area-inset-top, 0px)' : undefined,
                  paddingBottom: showMobileTabBar ? '0px' : undefined,
                }}
              >
                {children}
              </main>
            {showMobileTabBar ? <MobileTabBar /> : null}
        </div>
        </ExploreHeaderExtensionProvider>
    );
};

export default LayoutWrapper;
